/**
 * Voting, and specifically the third button (docs/01 §4.3).
 *
 * A proposal has three answers, not two:
 *
 *   Approve
 *   No — try another time   → a reject carrying a COUNTER-PROPOSAL
 *   No — explain            → a reject carrying a reason
 *
 * A bare yes/no leaves the requester guessing, and any reject resolves the
 * proposal immediately (§4.2) — so the moment you say no, the only thing left
 * to go on is whatever you attached to it. Both no-paths therefore require
 * text; the submit button stays disabled until there is some.
 *
 * The alternative is not a mutation of the original. Accepting it later creates
 * a NEW proposal linked by `supersedesId`, so the decision log stays honest
 * about what was actually proposed and what was actually agreed.
 */

import { useId, useState } from 'react'
import type { FormEvent } from 'react'
import { useDialogFocus } from '../chores/dialog.js'
import '../chores/phone.css'
import './proposals.css'

export type RejectMode = 'alternative' | 'reason'

export interface VoteSheetProps {
  mode: RejectMode
  proposalTitle: string
  /** mode 'alternative': the suggestion, plus an optional note. */
  onAlternative(suggestion: string, note: string): void
  /** mode 'reason': why not. */
  onReason(reason: string): void
  onClose(): void
}

export function VoteSheet({
  mode,
  proposalTitle,
  onAlternative,
  onReason,
  onClose,
}: VoteSheetProps) {
  const [main, setMain] = useState('')
  const [note, setNote] = useState('')
  const titleId = useId()
  const mainId = useId()
  const noteId = useId()
  const hintId = useId()
  const { containerRef, initialRef, onKeyDown } = useDialogFocus<
    HTMLFormElement,
    HTMLTextAreaElement
  >(onClose)

  const ready = main.trim() !== ''

  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (!ready) return
    if (mode === 'alternative') onAlternative(main.trim(), note)
    else onReason(main.trim())
  }

  const heading = mode === 'alternative' ? 'Suggest another time' : 'Say why not'
  const label = mode === 'alternative' ? 'What would work instead?' : 'Why not?'
  const hint =
    mode === 'alternative'
      ? 'They can raise your suggestion as a new proposal with one tap.'
      : 'A bare no leaves them guessing. One line is enough.'
  const placeholder = mode === 'alternative' ? 'After 4pm works' : 'Clashes with the client call'

  return (
    <div className="ph-sheet-backdrop">
      <form
        ref={containerRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="card stack gap-3 ph-sheet"
        onSubmit={submit}
        onKeyDown={onKeyDown}
      >
        <div>
          <h2 id={titleId} className="ph-sheet-title">
            {heading}
          </h2>
          <p className="ph-sheet-sub">{proposalTitle}</p>
        </div>

        <div className="stack gap-1">
          <label htmlFor={mainId} className="ph-field-label">
            {label}
          </label>
          <textarea
            ref={initialRef}
            id={mainId}
            className="ph-textarea"
            rows={3}
            required
            aria-describedby={hintId}
            value={main}
            onChange={e => setMain(e.target.value)}
            placeholder={placeholder}
          />
          <p id={hintId} className="ph-hint">
            {hint}
          </p>
        </div>

        {mode === 'alternative' ? (
          <div className="stack gap-1">
            <label htmlFor={noteId} className="ph-field-label">
              Anything else? (optional)
            </label>
            <textarea
              id={noteId}
              className="ph-textarea"
              rows={2}
              value={note}
              onChange={e => setNote(e.target.value)}
            />
          </div>
        ) : null}

        <div className="ph-sheet-actions">
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={!ready}>
            Send
          </button>
        </div>
      </form>
    </div>
  )
}
